
import React, { useState, useEffect, useMemo } from 'react';
import { fuzzySearch } from '../../utils/fuzzySearch';
import PrimaryButton from '../PrimaryButton';
import styles from './styles/PromotionCrud.module.css';
import {
    FaCheck,
    FaEdit,
    FaImage,
    FaLayerGroup,
    FaPlus,
    FaSearch,
    FaShoppingBasket,
    FaTrash
} from 'react-icons/fa';
import { Product } from '../../types';
import { getMainImage, getProductImageUrls } from '../../utils';

interface PromotionItem {
    id_producto: number;
    nombre: string;
    cantidad: number;
}

// Grupo de eleccion: "Elegi N de estas opciones".
// Ej: { nombre: "Pizzas", cantidad_elegir: 4, opciones: [pizzaA, pizzaB, ...] }
interface PromoGrupoOpcion {
    id_producto: number;
    nombre: string;
}
interface PromoGrupo {
    nombre: string;
    cantidad_elegir: number;
    opciones: PromoGrupoOpcion[];
}

// ─── Editor de Grupos de Eleccion ───────────────────────────────────────────
// Reutilizable en crear y editar. Permite definir varios grupos como
// "Elegi 4 pizzas (de estas 4 variantes)", "Elegi 2 vinos (de estos 5)".
interface GruposEditorProps {
    grupos: PromoGrupo[];
    setGrupos: React.Dispatch<React.SetStateAction<PromoGrupo[]>>;
    products: Product[];
}

const GruposEditor: React.FC<GruposEditorProps> = ({ grupos, setGrupos, products }) => {
    // Buscador por grupo (cada grupo tiene su propio campo de busqueda de opciones).
    const [searchByGroup, setSearchByGroup] = useState<Record<number, string>>({});

    const addGrupo = () => {
        setGrupos(prev => [...prev, { nombre: '', cantidad_elegir: 1, opciones: [] }]);
    };

    const removeGrupo = (idx: number) => {
        setGrupos(prev => prev.filter((_, i) => i !== idx));
    };

    const updateGrupo = (idx: number, patch: Partial<PromoGrupo>) => {
        setGrupos(prev => prev.map((g, i) => i === idx ? { ...g, ...patch } : g));
    };

    const addOpcion = (idx: number, prod: Product) => {
        setGrupos(prev => prev.map((g, i) => {
            if (i !== idx) return g;
            if (g.opciones.some(o => o.id_producto === prod.idProductos)) return g; // ya esta
            return { ...g, opciones: [...g.opciones, { id_producto: prod.idProductos, nombre: prod.NombresProductos }] };
        }));
        setSearchByGroup(prev => ({ ...prev, [idx]: '' }));
    };

    const removeOpcion = (idx: number, id_producto: number) => {
        setGrupos(prev => prev.map((g, i) =>
            i === idx ? { ...g, opciones: g.opciones.filter(o => o.id_producto !== id_producto) } : g
        ));
    };

    return (
        <div className={styles['groups-editor']}>
            {grupos.length === 0 && (
                <div className={styles['optional-state']}>
                    <FaLayerGroup />
                    <div>
                        <strong>La promo no tiene grupos de eleccion</strong>
                        <span>Agrega uno si el cliente debe elegir sabores, variedades o presentaciones.</span>
                    </div>
                </div>
            )}

            {grupos.map((grupo, idx) => {
                const groupSearch = searchByGroup[idx] || '';
                const filtered = groupSearch
                    ? fuzzySearch(products, groupSearch, { keys: ['NombresProductos'], threshold: 0.35 })
                    : [];

                return (
                    <article className={styles['group-card']} key={idx}>
                        <div className={styles['group-card-header']}>
                            <span>Grupo {idx + 1}</span>
                            <button type="button" onClick={() => removeGrupo(idx)}>
                                <FaTrash /> Quitar
                            </button>
                        </div>

                        <div className={styles['group-fields']}>
                            <label className={styles.field}>
                                <span>Nombre del grupo</span>
                                <input
                                    type="text"
                                    placeholder="Ej: Vinos"
                                    value={grupo.nombre}
                                    onChange={e => updateGrupo(idx, { nombre: e.target.value })}
                                />
                            </label>
                            <label className={styles.field}>
                                <span>Cuantos puede elegir</span>
                                <input
                                    type="number"
                                    min={1}
                                    value={grupo.cantidad_elegir}
                                    onChange={e => updateGrupo(idx, { cantidad_elegir: Math.max(1, parseInt(e.target.value) || 1) })}
                                />
                            </label>
                        </div>

                        <label className={`${styles.field} ${styles['group-search']}`}>
                            <span>Opciones disponibles</span>
                            <div className={styles['search-box']}>
                                <FaSearch />
                                <input
                                    type="text"
                                    placeholder="Buscar producto para agregar..."
                                    value={groupSearch}
                                    onChange={e => setSearchByGroup(prev => ({ ...prev, [idx]: e.target.value }))}
                                />
                            </div>
                        </label>

                        {groupSearch && (
                            <div className={styles['group-results']}>
                                {filtered.slice(0, 12).map(product => (
                                    <button type="button" key={product.idProductos} onClick={() => addOpcion(idx, product)}>
                                        <span>{product.NombresProductos}</span>
                                        <FaPlus />
                                    </button>
                                ))}
                                {filtered.length === 0 && <span>No encontramos productos.</span>}
                            </div>
                        )}

                        <div className={styles['option-chips']}>
                            {grupo.opciones.map(option => (
                                <button type="button" key={option.id_producto} onClick={() => removeOpcion(idx, option.id_producto)}>
                                    {option.nombre} <span>x</span>
                                </button>
                            ))}
                            {grupo.opciones.length === 0 && <small>Sin opciones agregadas.</small>}
                        </div>

                        <div className={`${styles['group-status']} ${grupo.opciones.length > 0 ? styles.valid : ''}`}>
                            <FaCheck />
                            {grupo.opciones.length > 0
                                ? `${grupo.opciones.length} opciones para elegir ${grupo.cantidad_elegir}`
                                : 'Agrega al menos una opcion para completar este grupo'}
                        </div>
                    </article>
                );
            })}

            <button type="button" className={styles['add-group']} onClick={addGrupo}>
                <FaPlus /> Agregar grupo de eleccion
            </button>
        </div>
    );
};

const PromotionCrud: React.FC = () => {
    const [promotions, setPromotions] = useState<Product[]>([]); // Promos are products
    const [products, setProducts] = useState<Product[]>([]);

    // Form State (Create)
    const [name, setName] = useState('');
    const [price, setPrice] = useState('');
    const [desc, setDesc] = useState('');
    const [imgUrl, setImgUrl] = useState('');
    const [subcats, setSubcats] = useState<any[]>([]);
    const [idSubcat, setIdSubcat] = useState('');

    // Logic for items
    const [search, setSearch] = useState('');
    const [productCategory, setProductCategory] = useState('');
    const [promotionSearch, setPromotionSearch] = useState('');
    const [selectedItems, setSelectedItems] = useState<PromotionItem[]>([]);
    const [grupos, setGrupos] = useState<PromoGrupo[]>([]); // grupos de eleccion (crear)
    const [creating, setCreating] = useState(false);
    const [notice, setNotice] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

    // Edit State
    const [editingPromo, setEditingPromo] = useState<Product | null>(null);
    const [editName, setEditName] = useState('');
    const [editPrice, setEditPrice] = useState('');
    const [editDesc, setEditDesc] = useState('');
    const [editImgUrl, setEditImgUrl] = useState('');
    const [editIdSubcat, setEditIdSubcat] = useState('');
    const [editSearch, setEditSearch] = useState('');
    const [editItems, setEditItems] = useState<PromotionItem[]>([]);
    const [editGrupos, setEditGrupos] = useState<PromoGrupo[]>([]); // grupos de eleccion (editar)
    const [editLoading, setEditLoading] = useState(false);

    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

    const fetchData = async () => {
        try {
            const resProd = await fetch(`${API_URL}/productos`);
            const dataProd = await resProd.json();

            // Separate Products and Promos
            setProducts(dataProd.filter((p: any) => !p.es_promocion));
            setPromotions(dataProd.filter((p: any) => p.es_promocion));

            const resSub = await fetch(`${API_URL}/subcategorias`);
            const dataSub = await resSub.json();
            setSubcats(dataSub);

        } catch (err) {
            console.error(err);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const addItem = (prod: Product) => {
        const existing = selectedItems.find(i => i.id_producto === prod.idProductos);
        if (existing) {
            setSelectedItems(prev => prev.map(i => i.id_producto === prod.idProductos ? { ...i, cantidad: i.cantidad + 1 } : i));
        } else {
            setSelectedItems([...selectedItems, { id_producto: prod.idProductos, nombre: prod.NombresProductos, cantidad: 1 }]);
        }
        setSearch(''); // Clear search after adding
    };

    const removeItem = (id: number) => {
        setSelectedItems(prev => prev.filter(i => i.id_producto !== id));
    };

    const handleCreate = async (e: React.FormEvent) => {
        e.preventDefault();
        setNotice(null);
        if (!name.trim() || !price || Number(price) <= 0) {
            setNotice({ type: 'error', text: 'Completa el nombre y un precio mayor a cero.' });
            return;
        }
        if (selectedItems.length === 0 && grupos.length === 0) {
            setNotice({ type: 'error', text: 'Agrega al menos un producto fijo o un grupo de eleccion.' });
            return;
        }
        const grupoInvalido = grupos.find(g => !g.nombre.trim() || g.opciones.length === 0);
        if (grupoInvalido) {
            setNotice({ type: 'error', text: 'Cada grupo necesita un nombre y al menos una opcion.' });
            return;
        }

        const payload = {
            nombre: name,
            precio: parseFloat(price),
            descripcion: desc,
            idSubcategoria: idSubcat || null,
            UrlImagen: imgUrl.trim() || null,  // Opcional: si va vacio, hereda las imagenes de sus productos
            items: selectedItems,
            grupos: grupos.map(g => ({
                nombre: g.nombre.trim(),
                cantidad_elegir: g.cantidad_elegir,
                opciones: g.opciones.map(o => o.id_producto)
            }))
        };

        setCreating(true);
        try {
            const res = await fetch(`${API_URL}/admin/promociones`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                setName('');
                setPrice('');
                setDesc('');
                setImgUrl('');
                setIdSubcat('');
                setSelectedItems([]);
                setGrupos([]);
                setSearch('');
                setProductCategory('');
                setNotice({ type: 'success', text: 'Promocion creada correctamente.' });
                fetchData();
            } else {
                const errorData = await res.json().catch(() => ({}));
                setNotice({ type: 'error', text: errorData.error || 'No se pudo crear la promocion.' });
            }
        } catch (err) {
            console.error(err);
            setNotice({ type: 'error', text: 'No se pudo conectar con el backend.' });
        } finally {
            setCreating(false);
        }
    };

    // ─── Editar Promoción ───────────────────────────────────────────────────────

    const handleOpenEdit = async (promo: Product) => {
        setEditingPromo(promo);
        setEditName(promo.NombresProductos);
        setEditPrice(String(promo.Precio));
        setEditDesc(promo.descripcion || '');
        // Solo precargar si es imagen propia; si es heredada, dejar vacio para que siga heredando
        setEditImgUrl(promo.imagen_heredada ? '' : (promo.UrlImagen || ''));
        setEditIdSubcat(promo.fk_idSubcategoria ? String(promo.fk_idSubcategoria) : '');
        setEditSearch('');
        setEditLoading(true);

        // Fetch current items of the promotion
        try {
            const res = await fetch(`${API_URL}/admin/promociones/${promo.idProductos}`);
            if (res.ok) {
                const data = await res.json();
                // Nuevo formato: { items: [...], grupos: [...] }. Compat: array suelto = items.
                const items = Array.isArray(data) ? data : (data.items || []);
                const gruposApi = Array.isArray(data) ? [] : (data.grupos || []);
                setEditItems(items);
                setEditGrupos(gruposApi.map((g: any) => ({
                    nombre: g.nombre || '',
                    cantidad_elegir: g.cantidad_elegir || 1,
                    opciones: (g.opciones || []).map((o: any) => ({ id_producto: o.id_producto, nombre: o.nombre }))
                })));
            } else {
                setEditItems([]);
                setEditGrupos([]);
            }
        } catch {
            setEditItems([]);
            setEditGrupos([]);
        } finally {
            setEditLoading(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingPromo(null);
        setEditItems([]);
        setEditGrupos([]);
        setEditSearch('');
    };

    const addEditItem = (prod: Product) => {
        const existing = editItems.find(i => i.id_producto === prod.idProductos);
        if (existing) {
            setEditItems(prev => prev.map(i => i.id_producto === prod.idProductos ? { ...i, cantidad: i.cantidad + 1 } : i));
        } else {
            setEditItems([...editItems, { id_producto: prod.idProductos, nombre: prod.NombresProductos, cantidad: 1 }]);
        }
        setEditSearch('');
    };

    const removeEditItem = (id: number) => {
        setEditItems(prev => prev.filter(i => i.id_producto !== id));
    };

    const handleSaveEdit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!editingPromo) return;
        if (editItems.length === 0 && editGrupos.length === 0) {
            alert("La promoción debe tener al menos un producto fijo o un grupo de elección");
            return;
        }
        const grupoInvalido = editGrupos.find(g => !g.nombre.trim() || g.opciones.length === 0);
        if (grupoInvalido) {
            alert("Cada grupo de elección necesita un nombre y al menos una opción");
            return;
        }

        const payload = {
            nombre: editName,
            precio: parseFloat(editPrice),
            descripcion: editDesc,
            idSubcategoria: editIdSubcat || null,
            UrlImagen: editImgUrl.trim() || null,  // Vacio => vuelve a heredar las imagenes de sus productos
            items: editItems,
            grupos: editGrupos.map(g => ({
                nombre: g.nombre.trim(),
                cantidad_elegir: g.cantidad_elegir,
                opciones: g.opciones.map(o => o.id_producto)
            }))
        };

        try {
            const res = await fetch(`${API_URL}/admin/promociones/${editingPromo.idProductos}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) {
                alert("Promoción actualizada con éxito");
                handleCancelEdit();
                fetchData();
            } else {
                const errData = await res.json().catch(() => ({}));
                alert(errData.error || "Error al actualizar la promoción");
            }
        } catch (err) {
            console.error(err);
            alert("Error de conexión");
        }
    };

    // ─── Eliminar Promoción ─────────────────────────────────────────────────────

    const handleDelete = async (promo: Product) => {
        const confirmed = window.confirm(
            `¿Estás seguro de que querés eliminar la promoción "${promo.NombresProductos}"?\n\nEsto eliminará la promoción del sistema. El stock de sus componentes NO se verá afectado.`
        );
        if (!confirmed) return;

        try {
            const res = await fetch(`${API_URL}/admin/promociones/${promo.idProductos}`, {
                method: 'DELETE'
            });
            if (res.ok) {
                alert("Promoción eliminada con éxito");
                fetchData();
            } else {
                const errData = await res.json().catch(() => ({}));
                alert(errData.error || "Error al eliminar la promoción");
            }
        } catch (err) {
            console.error(err);
            alert("Error de conexión");
        }
    };

    const filteredProducts = useMemo(() => {
        const matches = fuzzySearch(products, search, {
            keys: ['NombresProductos'],
            threshold: 0.35
        });
        return matches.filter(product => !productCategory || product.nombre_subcategoria === productCategory);
    }, [products, search, productCategory]);

    const visiblePromotions = useMemo(() => {
        return fuzzySearch(promotions, promotionSearch, {
            keys: ['NombresProductos', 'descripcion'],
            threshold: 0.35
        });
    }, [promotions, promotionSearch]);

    const productCategories = useMemo(() => {
        return Array.from(new Set(products.map(product => product.nombre_subcategoria).filter(Boolean))).sort();
    }, [products]);

    const filteredEditProducts = useMemo(() => {
        return fuzzySearch(products, editSearch, {
            keys: ['NombresProductos'],
            threshold: 0.35
        });
    }, [products, editSearch]);

    const formReady = Boolean(
        name.trim() &&
        Number(price) > 0 &&
        (selectedItems.length > 0 || grupos.length > 0) &&
        grupos.every(grupo => grupo.nombre.trim() && grupo.opciones.length > 0)
    );

    const totalFixedUnits = selectedItems.reduce((total, item) => total + item.cantidad, 0);
    const previewImage = getMainImage(imgUrl);

    return (
        <div className={styles['promotion-crud-container']}>
            <h3>Gestor de Promociones / Combos</h3>

            {/* ─── Modal/Inline Panel de Edición ─────────────────────────────── */}
            {editingPromo && (
                <div className={styles['edit-overlay']}>
                    <div className={styles['edit-panel']}>
                        <div className={styles['edit-panel-header']}>
                            <h4>✏️ Editar Promoción</h4>
                            <button className={styles['btn-close']} onClick={handleCancelEdit}>✕</button>
                        </div>

                        {editLoading ? (
                            <p>Cargando datos...</p>
                        ) : (
                            <form onSubmit={handleSaveEdit}>
                                <div className={styles['form-grid']}>
                                    <div className={styles['form-group']}>
                                        <label>Nombre Promoción</label>
                                        <input type="text" value={editName} onChange={e => setEditName(e.target.value)} required />
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>Precio Final</label>
                                        <input type="number" value={editPrice} onChange={e => setEditPrice(e.target.value)} required />
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>Descripción</label>
                                        <input type="text" value={editDesc} onChange={e => setEditDesc(e.target.value)} />
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>Imagen propia (Opcional)</label>
                                        <input
                                            type="text"
                                            value={editImgUrl}
                                            onChange={e => setEditImgUrl(e.target.value)}
                                            placeholder="https://... (podés poner varias URLs separadas por coma)"
                                        />
                                        <small style={{ color: '#999', fontSize: '0.78rem' }}>
                                            Si lo dejás vacío, la promoción usa las imágenes de sus productos.
                                        </small>
                                    </div>
                                    <div className={styles['form-group']}>
                                        <label>Subcategoría (Opcional)</label>
                                        <select value={editIdSubcat} onChange={e => setEditIdSubcat(e.target.value)}>
                                            <option value="">-- Sin categoría --</option>
                                            {subcats.map(s => (
                                                <option key={s.idSubcategoria} value={s.idSubcategoria}>{s.nombre} ({s.nombre_categoria})</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className={styles['form-group']}>
                                        <label>Agregar / Modificar Productos:</label>
                                        <input
                                            type="text"
                                            placeholder="Buscar producto..."
                                            value={editSearch}
                                            onChange={e => setEditSearch(e.target.value)}
                                        />
                                        {editSearch && (
                                            <ul className={styles['search-results']}>
                                                {filteredEditProducts.map(p => (
                                                    <li key={p.idProductos} onClick={() => addEditItem(p)}>
                                                        <span>{p.NombresProductos}</span>
                                                        <span style={{ fontWeight: 'bold', color: 'green' }}>+</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>

                                    <div style={{ marginBottom: '20px' }}>
                                        <h5>Productos fijos (siempre incluidos):</h5>
                                        {editItems.length === 0 && <p style={{ color: '#999' }}>Sin productos fijos.</p>}
                                        <ul className={styles['items-list']}>
                                            {editItems.map(item => (
                                                <li key={item.id_producto} style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '5px' }}>
                                                    <span style={{ flex: 1 }}>{item.nombre}</span>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        value={item.cantidad}
                                                        style={{ width: '60px' }}
                                                        onChange={(e) => {
                                                            const newQty = parseInt(e.target.value);
                                                            setEditItems(prev => prev.map(i => i.id_producto === item.id_producto ? { ...i, cantidad: newQty } : i));
                                                        }}
                                                    />
                                                    <button type="button" onClick={() => removeEditItem(item.id_producto)} style={{ color: 'red' }}>X</button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>

                                    <GruposEditor grupos={editGrupos} setGrupos={setEditGrupos} products={products} />

                                    <div className={styles['edit-actions']}>
                                        <button type="button" className={styles['btn-cancel']} onClick={handleCancelEdit}>Cancelar</button>
                                        <PrimaryButton type="submit">Guardar Cambios</PrimaryButton>
                                    </div>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* ─── Formulario de Creación ─────────────────────────────────────── */}
            <div className={styles['page-intro']}>
                <div>
                    <span className={styles.eyebrow}>Nueva promocion</span>
                    <h3>Arma la promocion paso a paso</h3>
                    <p>Completa los datos, agrega productos y revisa el resumen antes de guardar.</p>
                </div>
            </div>

            {notice && (
                <div className={`${styles.notice} ${styles[notice.type]}`} role="status">
                    {notice.text}
                </div>
            )}

            <form onSubmit={handleCreate} className={styles.workspace}>
                <div className={styles['form-column']}>
                    <section className={styles.panel}>
                        <div className={styles['section-header']}>
                            <span className={styles.step}>1</span>
                            <div>
                                <h4>Datos principales</h4>
                                <p>Esta informacion sera visible para el cliente.</p>
                            </div>
                        </div>

                        <div className={styles['field-grid']}>
                            <label className={`${styles.field} ${styles['full-field']}`}>
                                <span>Nombre de la promocion *</span>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    placeholder="Ej: Combo familiar de campo"
                                    required
                                />
                            </label>

                            <label className={styles.field}>
                                <span>Precio final *</span>
                                <div className={styles['money-input']}>
                                    <span>$</span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={price}
                                        onChange={e => setPrice(e.target.value)}
                                        placeholder="0"
                                        required
                                    />
                                </div>
                            </label>

                            <label className={styles.field}>
                                <span>Subcategoria</span>
                                <select value={idSubcat} onChange={e => setIdSubcat(e.target.value)}>
                                    <option value="">Sin subcategoria</option>
                                    {subcats.map(s => (
                                        <option key={s.idSubcategoria} value={s.idSubcategoria}>
                                            {s.nombre_categoria ? `${s.nombre_categoria} / ${s.nombre}` : s.nombre}
                                        </option>
                                    ))}
                                </select>
                            </label>

                            <label className={`${styles.field} ${styles['full-field']}`}>
                                <span>Descripcion breve</span>
                                <textarea
                                    value={desc}
                                    onChange={e => setDesc(e.target.value)}
                                    placeholder="Explica en una frase que incluye o por que conviene."
                                    rows={3}
                                />
                            </label>

                            <label className={`${styles.field} ${styles['full-field']}`}>
                                <span>Imagen propia (opcional)</span>
                                <div className={styles['icon-input']}>
                                    <FaImage />
                                    <input
                                        type="text"
                                        value={imgUrl}
                                        onChange={e => setImgUrl(e.target.value)}
                                        placeholder="https://..."
                                    />
                                </div>
                                <small>Si queda vacia, la promo usa las imagenes de sus productos fijos.</small>
                            </label>
                        </div>
                    </section>

                    <section className={styles.panel}>
                        <div className={styles['section-header']}>
                            <span className={styles.step}>2</span>
                            <div>
                                <h4>Productos fijos</h4>
                                <p>Son los productos que siempre se entregan dentro de la promocion.</p>
                            </div>
                        </div>

                        {selectedItems.length > 0 ? (
                            <div className={styles['selected-products']}>
                                {selectedItems.map(item => {
                                    const product = products.find(p => p.idProductos === item.id_producto);
                                    return (
                                        <div className={styles['selected-product']} key={item.id_producto}>
                                            <ProductThumb product={product} name={item.nombre} />
                                            <div className={styles['selected-product-info']}>
                                                <strong>{item.nombre}</strong>
                                                <small>{product?.nombre_subcategoria || 'Sin subcategoria'}</small>
                                            </div>
                                            <div className={styles['quantity-control']}>
                                                <button
                                                    type="button"
                                                    onClick={() => item.cantidad <= 1
                                                        ? removeItem(item.id_producto)
                                                        : setSelectedItems(prev => prev.map(i => i.id_producto === item.id_producto ? { ...i, cantidad: i.cantidad - 1 } : i))}
                                                >
                                                    -
                                                </button>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={item.cantidad}
                                                    onChange={e => {
                                                        const quantity = Math.max(1, parseInt(e.target.value) || 1);
                                                        setSelectedItems(prev => prev.map(i => i.id_producto === item.id_producto ? { ...i, cantidad: quantity } : i));
                                                    }}
                                                    aria-label={`Cantidad de ${item.nombre}`}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedItems(prev => prev.map(i => i.id_producto === item.id_producto ? { ...i, cantidad: i.cantidad + 1 } : i))}
                                                >
                                                    +
                                                </button>
                                            </div>
                                            <button
                                                type="button"
                                                className={styles['remove-item']}
                                                onClick={() => removeItem(item.id_producto)}
                                                aria-label={`Quitar ${item.nombre}`}
                                            >
                                                <FaTrash />
                                            </button>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className={styles['empty-state']}>
                                <FaShoppingBasket />
                                <strong>Todavia no agregaste productos fijos</strong>
                                <span>Puedes buscarlos en el selector de abajo.</span>
                            </div>
                        )}

                        <div className={styles['product-picker']}>
                            <div className={styles['picker-title']}>
                                <div>
                                    <h5>Agregar productos</h5>
                                    <p>{filteredProducts.length} productos disponibles</p>
                                </div>
                            </div>
                            <div className={styles.filters}>
                                <label className={styles['search-box']}>
                                    <FaSearch />
                                    <input
                                        type="text"
                                        placeholder="Buscar por nombre..."
                                        value={search}
                                        onChange={e => setSearch(e.target.value)}
                                    />
                                </label>
                                <select value={productCategory} onChange={e => setProductCategory(e.target.value)}>
                                    <option value="">Todas las subcategorias</option>
                                    {productCategories.map(category => (
                                        <option key={category as string} value={category as string}>{category}</option>
                                    ))}
                                </select>
                            </div>
                            <div className={styles['picker-list']}>
                                {filteredProducts.slice(0, 60).map(product => {
                                    const selected = selectedItems.find(item => item.id_producto === product.idProductos);
                                    return (
                                        <div className={styles['picker-row']} key={product.idProductos}>
                                            <ProductThumb product={product} name={product.NombresProductos} />
                                            <div className={styles['picker-product-info']}>
                                                <strong>{product.NombresProductos}</strong>
                                                <small>
                                                    {product.nombre_subcategoria || 'Sin subcategoria'} · ${Number(product.Precio || 0).toLocaleString('es-AR')}
                                                </small>
                                            </div>
                                            <button
                                                type="button"
                                                className={selected ? styles['add-again'] : styles['add-product']}
                                                onClick={() => addItem(product)}
                                            >
                                                <FaPlus /> {selected ? `Sumar (${selected.cantidad})` : 'Agregar'}
                                            </button>
                                        </div>
                                    );
                                })}
                                {filteredProducts.length === 0 && (
                                    <div className={styles['empty-results']}>No encontramos productos con esos filtros.</div>
                                )}
                            </div>
                        </div>
                    </section>

                    <section className={styles.panel}>
                        <div className={styles['section-header']}>
                            <span className={styles.step}>3</span>
                            <div>
                                <h4>Grupos de eleccion</h4>
                                <p>Opcional. Usalos cuando el cliente puede elegir variantes.</p>
                            </div>
                        </div>
                        <GruposEditor grupos={grupos} setGrupos={setGrupos} products={products} />
                    </section>
                </div>

                <aside className={styles['summary-column']}>
                    <section className={`${styles.panel} ${styles['summary-card']}`}>
                        <span className={styles.eyebrow}>Vista previa</span>
                        <div className={styles['preview-image']}>
                            {previewImage ? <img loading="lazy" decoding="async" src={previewImage} alt="Vista previa de la promocion" /> : <FaImage />}
                        </div>
                        <h4>{name.trim() || 'Nombre de la promocion'}</h4>
                        <strong className={styles['preview-price']}>
                            ${Number(price || 0).toLocaleString('es-AR')}
                        </strong>
                        <p>{totalFixedUnits} unidades fijas · {grupos.length} grupos de eleccion</p>

                        <div className={styles.checklist}>
                            <span className={name.trim() && Number(price) > 0 ? styles.ready : ''}>
                                <FaCheck /> Datos principales
                            </span>
                            <span className={selectedItems.length > 0 || grupos.length > 0 ? styles.ready : ''}>
                                <FaCheck /> Productos configurados
                            </span>
                            <span className={grupos.every(g => g.nombre.trim() && g.opciones.length > 0) ? styles.ready : ''}>
                                <FaCheck /> Grupos validos
                            </span>
                        </div>

                        <button type="submit" className={styles['save-button']} disabled={!formReady || creating}>
                            {creating ? 'Guardando...' : 'Crear promocion'}
                        </button>
                        <small className={styles['save-help']}>
                            {formReady ? 'Todo listo para guardar.' : 'Completa los puntos pendientes para guardar.'}
                        </small>
                    </section>
                </aside>
            </form>

            {/* ─── Tabla de Promociones Activas ───────────────────────────────── */}
            <section className={`${styles.panel} ${styles['promotions-section']}`}>
                <div className={styles['list-header']}>
                    <div>
                        <span className={styles.eyebrow}>Administrar</span>
                        <h4>Promociones activas</h4>
                        <p>Se venden como productos y descuentan el stock de sus componentes.</p>
                    </div>
                    <label className={styles['search-box']}>
                        <FaSearch />
                        <input
                            type="text"
                            value={promotionSearch}
                            onChange={e => setPromotionSearch(e.target.value)}
                            placeholder="Buscar promocion..."
                        />
                    </label>
                </div>

                <div className={styles['promotion-list']}>
                    {visiblePromotions.map(promo => (
                        <article className={styles['promotion-row']} key={promo.idProductos}>
                            <ProductThumb product={promo} name={promo.NombresProductos} />
                            <div className={styles['promotion-info']}>
                                <strong>{promo.NombresProductos}</strong>
                                <small>{promo.descripcion || promo.nombre_subcategoria || 'Sin descripcion'}</small>
                            </div>
                            <strong className={styles['promotion-price']}>
                                ${Number(promo.Precio || 0).toLocaleString('es-AR')}
                            </strong>
                            <div className={styles['action-buttons']}>
                                <button className={styles['btn-edit']} onClick={() => handleOpenEdit(promo)}>
                                    <FaEdit /> Editar
                                </button>
                                <button className={styles['btn-delete']} onClick={() => handleDelete(promo)}>
                                    <FaTrash /> Eliminar
                                </button>
                            </div>
                        </article>
                    ))}
                    {visiblePromotions.length === 0 && (
                        <div className={styles['empty-results']}>No hay promociones que coincidan con la busqueda.</div>
                    )}
                </div>
            </section>
        </div>
    );
};

const ProductThumb: React.FC<{ product?: Product; name: string }> = ({ product, name }) => {
    const image = getProductImageUrls(product)[0];

    return image ? (
        <img loading="lazy" decoding="async" className={styles['product-thumb']} src={image} alt="" />
    ) : (
        <span className={styles['product-placeholder']}>{name.charAt(0).toUpperCase()}</span>
    );
};

export default PromotionCrud;
